import { Cookies } from 'react-cookie';
import { isLoggedKey } from '../util/constants';
import { decodeJWT } from '../util/decodeJWT';
import { logger } from '../util/logger';
import { setAuthStateForAnalytics } from '../util/posthog';
import { isEmbeddedMode } from './embedMode';
import { logout, logoutWithoutApi } from './logoutUtils';
import { isPartialAuthActive } from './partialAuthStorage';
import { getCurrentClientUserManager, getCurrentUser } from './userManager';

const SILENT_SSO_TIMEOUT_MS = 1000;

export const AUTOLOGIN_JWT_PENDING_KEY = 'autologin_jwt_pending';
export const VENDER_LOGIN_KEY = 'vendor-auto-login';

function setVenderLoginFlagFromAutologinToken(autologinJwt: string): void {
  const decodedToken = decodeJWT(autologinJwt);
  if (decodedToken?.['sso-method'] === VENDER_LOGIN_KEY) {
    localStorage.setItem(VENDER_LOGIN_KEY, 'true');
  }
}

function clearIsLoggedInCookieForAutoLogin(): void {
  const cookies = new Cookies();
  cookies.remove(isLoggedKey, { path: '/' });
}

// ── Auto-login session override ──────────────────────────────────────────────

/**
 * When `?autologin=` is present, perform a complete logout first so any
 * server-side session is ended (including Keycloak SSO when applicable).
 * The JWT is stashed in sessionStorage so Login's handleInitialRedirect
 * can pick it up after the redirect back to `/login`.
 */
async function clearExistingSessionForAutoLogin(): Promise<boolean> {
  const parameters = new URLSearchParams(window.location.search);
  if (!parameters.has('autologin')) return false;
  clearIsLoggedInCookieForAutoLogin();

  const autologinJwt = parameters.get('autologin')!;
  setVenderLoginFlagFromAutologinToken(autologinJwt);

  sessionStorage.setItem(AUTOLOGIN_JWT_PENDING_KEY, autologinJwt);
  const user = await getCurrentUser();
  if(!user){
    return false;
  };


  const isPartialVendorAutoLogin =
    isPartialAuthActive() && localStorage.getItem(VENDER_LOGIN_KEY) === 'true';
  logger.log('[OIDC] Auto-login — performing complete logout before autologin');
  await (isPartialVendorAutoLogin ? logoutWithoutApi() : logout(false));
  setAuthStateForAnalytics(false);
  return true;
}

/* eslint-disable unicorn/prefer-top-level-await -- resolved before silent SSO */
const autoLoginLogoutPromise: Promise<void> | null = (() => {
  const parameters = new URLSearchParams(window.location.search);
  if (!parameters.has('autologin')) return null;
  if (window.location.pathname !== '/login') {
    parameters.delete('autologin');
    const queryString = parameters.toString();
    localStorage.setItem(
      'pendingRedirectUrl',
      queryString
        ? `${window.location.pathname}?${queryString}`
        : window.location.pathname,
    );
  }
  return clearExistingSessionForAutoLogin().then(() => {});
})();
/* eslint-enable unicorn/prefer-top-level-await */

// ── Silent SSO ───────────────────────────────────────────────────────────────

/**
 * Pre-boot SSO probe — runs automatically on module evaluation so the check
 * starts as early as possible (during import resolution, before React mounts).
 *
 * Uses a tiered strategy for optimal performance:
 *  Tier 1: locally valid token in the active OIDC userStore (~1ms, no network)
 *  Tier 2: refresh-token grant via signinSilent (~100-200ms)
 *  Tier 3: iframe-based silent SSO via signinSilent (~200-600ms)
 */
async function performSilentSso(): Promise<void> {
  try {
    const { userManager: activeUserManager } = getCurrentClientUserManager();

    const existingUser = await activeUserManager.getUser();
    if (existingUser && !existingUser.expired && existingUser.access_token) {
      sessionStorage.setItem('silentSsoSuccess', 'true');
      logger.log('[OIDC] Tier 1 — locally valid token found (no network)');
      return;
    }

    const user = await Promise.race([
      activeUserManager.signinSilent(),
      new Promise<never>((_resolve, reject) => {
        setTimeout(() => reject(new Error('Silent SSO timeout')), SILENT_SSO_TIMEOUT_MS);
      }),
    ]);

    if (user?.access_token) {
      sessionStorage.setItem('silentSsoSuccess', 'true');
      logger.log('[OIDC] Silent SSO succeeded');
    }
  } catch (error) {
    logger.log('[OIDC] Silent SSO skipped or failed:', error);
  }
}

function shouldRunSilentSso(): boolean {
  if (isEmbeddedMode()) return false;
  if (process.env.REACT_APP_LOGIN_WITH_KEYCLOAK !== 'true') return false;
  if (document.cookie.includes('isLoggedIn')) return false;
  const parameters = new URLSearchParams(window.location.search);
  if (parameters.has('code') || parameters.has('session_state')) return false;
  if (parameters.has('error') || parameters.has('keycloak_error')) return false;
  if (sessionStorage.getItem('keycloak_logout_pending')) return false;
  if (sessionStorage.getItem('keycloak_pending_error')) return false;
  return true;
}

// ── Module-level init ────────────────────────────────────────────────────────
// Skip silent SSO when navigating to Keycloak logout, when autologin is in
// the URL, or when a pending autologin JWT is waiting in sessionStorage
// (returned from Keycloak logout redirect).

const hasAutologinIntent =
  new URLSearchParams(window.location.search).has('autologin') ||
  sessionStorage.getItem(AUTOLOGIN_JWT_PENDING_KEY) !== null;

/* eslint-disable unicorn/prefer-top-level-await, unicorn/prefer-logical-operator-over-ternary -- intentionally unresolved; AuthContext awaits it later; nested ternary is clearer than || chaining here */
const silentSsoPromise: Promise<void> | null =
  autoLoginLogoutPromise
    ? autoLoginLogoutPromise
    : hasAutologinIntent
      ? null
      : shouldRunSilentSso()
        ? performSilentSso()
        : null;
/* eslint-enable unicorn/prefer-top-level-await, unicorn/prefer-logical-operator-over-ternary */

/**
 * Returns the in-flight silent SSO promise (if any).
 * AuthContext awaits this so `loading` stays true until the check completes,
 * rather than flashing unauthenticated content.
 *
 * When auto-login is pending or navigating to Keycloak logout, resolves
 * immediately so the app boots fast.
 */
export function waitForSilentSso(): Promise<void> {
  return silentSsoPromise ?? Promise.resolve();
}
